import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { authApi, authMfaApi, ApiError, readToken, writeToken } from '@solara/api-client';
import type { UserDto } from '@solara/api-client';

/**
 * Customer Portal session.
 *
 * Backed by the Solara Core `/auth/signin` endpoint via @solara/api-client.
 * On successful sign-in the JWT is persisted to localStorage by the client,
 * and refreshed on app mount via `authApi.me()`.
 *
 * Demo accounts seeded by `services/core/prisma/seed.ts`:
 *  - admin@burj-holdings-llc.ae      / Budapest1928!  (CustomerAdmin · pilot)
 *  - accountant@burj-holdings-llc.ae / Budapest1928!  (Accountant)
 *  - approver@burj-holdings-llc.ae   / Budapest1928!  (Approver)
 *  - viewer@burj-holdings-llc.ae     / Budapest1928!  (Viewer)
 *  - customer@solara.ae              / Budapest1928!  (CustomerAdmin · platform tenant)
 */

const STORAGE_KEY = 'solara.portal.session.v1';

// Kept for the login page hint card. The actual user list lives in the DB.
export const DEMO_USERNAME = 'admin@burj-holdings-llc.ae';
export const DEMO_PASSWORD = 'Budapest1928!';

export interface SessionState {
  isAuthenticated: boolean;
  signedInAt: string | null;
  username: string | null;
  user: UserDto | null;
}

export type SignInResult =
  | { ok: true }
  | { ok: false; reason: 'required' | 'invalid' | 'wrong-portal' }
  | { ok: false; reason: 'mfa-required'; mfaChallengeToken: string };

export interface SessionApi extends SessionState {
  signIn: (username: string, password: string) => Promise<SignInResult>;
  /** Complete an MFA challenge with the 6-digit TOTP (or backup code). */
  completeMfa: (mfaChallengeToken: string, token: string) => Promise<{ ok: true } | { ok: false; reason: 'invalid' }>;
  signOut: () => void;
}

const Ctx = createContext<SessionApi | null>(null);

function loadFromStorage(): SessionState {
  if (typeof window === 'undefined') {
    return { isAuthenticated: false, signedInAt: null, username: null, user: null };
  }
  // Trust the JWT — if a token exists we'll validate it via `authApi.me()`
  // on mount; in the meantime treat the session as authenticated so the
  // SPA doesn't bounce the user to /login.
  const token = readToken();
  if (!token) return { isAuthenticated: false, signedInAt: null, username: null, user: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SessionState>;
      if (parsed?.username) {
        return {
          isAuthenticated: true,
          signedInAt: parsed.signedInAt ?? new Date().toISOString(),
          username: parsed.username,
          user: parsed.user ?? null,
        };
      }
    }
  } catch {
    /* ignore */
  }
  return { isAuthenticated: true, signedInAt: new Date().toISOString(), username: null, user: null };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(() => loadFromStorage());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (state.isAuthenticated) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [state]);

  useEffect(() => {
    if (!state.isAuthenticated || state.user) return;
    let cancelled = false;
    authApi
      .me()
      .then((u) => {
        if (cancelled) return;
        // Portal mismatch — the token belongs to a platform admin (left over
        // from the previous shared-token era). Boot them back to /login so the
        // session doesn't dribble 403s on every tenant-scoped endpoint.
        if (u.portal === 'ADMIN') {
          writeToken(null);
          setState({ isAuthenticated: false, signedInAt: null, username: null, user: null });
          return;
        }
        setState((s) => ({ ...s, user: u, username: u.email }));
      })
      .catch(() => {
        /* token was invalid — interceptor will have cleared it */
      });
    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated, state.user]);

  const signIn = useCallback<SessionApi['signIn']>(async (username, password) => {
    const u = username.trim();
    if (!u || !password) return { ok: false, reason: 'required' };
    try {
      const res = await authApi.signIn({ email: u, password });
      if (!res.accessToken && res.mfaChallengeToken) {
        return { ok: false, reason: 'mfa-required', mfaChallengeToken: res.mfaChallengeToken };
      }
      // Reject platform admins on the customer portal — they have tenantId=null
      // and every tenant-scoped endpoint would 403. Force them to the admin console.
      if (res.user.portal === 'ADMIN') {
        return { ok: false, reason: 'wrong-portal' };
      }
      writeToken(res.accessToken);
      setState({
        isAuthenticated: true,
        signedInAt: new Date().toISOString(),
        username: res.user.email,
        user: res.user,
      });
      return { ok: true };
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 401 || apiErr?.status === 403) return { ok: false, reason: 'invalid' };
      // Network / 500 — treat as invalid so the user sees an error message
      // and we don't lock them in a half-authenticated state.
      return { ok: false, reason: 'invalid' };
    }
  }, []);

  const completeMfa = useCallback<SessionApi['completeMfa']>(async (mfaChallengeToken, token) => {
    try {
      const res = await authMfaApi.verifyChallenge(mfaChallengeToken, token);
      writeToken(res.accessToken);
      setState({
        isAuthenticated: true,
        signedInAt: new Date().toISOString(),
        username: res.user.email,
        user: res.user,
      });
      return { ok: true };
    } catch {
      return { ok: false, reason: 'invalid' };
    }
  }, []);

  const signOut = useCallback(() => {
    writeToken(null);
    setState({ isAuthenticated: false, signedInAt: null, username: null, user: null });
  }, []);

  const value = useMemo<SessionApi>(
    () => ({ ...state, signIn, completeMfa, signOut }),
    [state, signIn, completeMfa, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
